/**
 * Normalized API error shape used by all fetchers.
 *
 * Spec: docs/architecture/data-layer-spec.md §4.
 */

export type ApiError =
  | { kind: "network"; message: string; cause?: unknown }
  | { kind: "auth"; message: string; status: 401 | 403; cause?: unknown }
  | {
      kind: "validation";
      message: string;
      status: 400 | 422;
      fields?: Record<string, string>;
      cause?: unknown;
    }
  | { kind: "server"; message: string; status: number; cause?: unknown }
  | { kind: "unknown"; message: string; cause?: unknown };

export class ApiErrorException extends Error {
  public readonly error: ApiError;

  constructor(error: ApiError) {
    super(error.message);
    this.name = "ApiErrorException";
    this.error = error;
  }
}

export function isApiError(e: unknown): e is ApiErrorException {
  return e instanceof ApiErrorException;
}

/**
 * Normalize any unknown error / Response into `ApiError`.
 *
 * - `TypeError` or raw `Error` (fetch failure) → `network`
 * - `Response` with 401/403 → `auth`
 * - `Response` with 400/422 → `validation`
 * - `Response` with 5xx → `server`
 * - anything else → `unknown`
 */
export function normalize(raw: unknown): ApiError {
  if (raw instanceof Response) {
    const status = raw.status;
    if (status === 401 || status === 403) {
      return {
        kind: "auth",
        status: status as 401 | 403,
        message: `Authentication required (${status})`,
        cause: raw,
      };
    }
    if (status === 400 || status === 422) {
      return {
        kind: "validation",
        status: status as 400 | 422,
        message: `Validation failed (${status})`,
        cause: raw,
      };
    }
    if (status >= 500) {
      return {
        kind: "server",
        status,
        message: `Server error (${status})`,
        cause: raw,
      };
    }
    return { kind: "unknown", message: `Unexpected response (${status})`, cause: raw };
  }

  if (raw instanceof TypeError) {
    return { kind: "network", message: raw.message || "Network error", cause: raw };
  }

  if (raw instanceof Error) {
    return { kind: "unknown", message: raw.message, cause: raw };
  }

  return { kind: "unknown", message: "Unknown error", cause: raw };
}
